/**
 * การ์ด Top 5 มาสาย / ลางาน ของงวด — ใส่ในแดชบอร์ด owner/HR.
 * เลือกงวดได้ (default = งวดล่าสุดที่มีข้อมูล).
 */
import { useState } from 'react';
import { Clock, CalendarOff } from 'lucide-react';
import { useAttendanceTop, type AttendanceTopRow } from '../hooks';

const TH_MON = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const periodLabel = (p: string) => {
  const [y, m] = p.split('-').map(Number);
  return `${TH_MON[m ?? 0] ?? ''} ${(y ?? 0) + 543}`;
};

function RankRow({ i, name, right }: { i: number; name: string; right: string }) {
  return (
    <div className="flex items-center gap-2 py-1.5">
      <span className={`w-5 h-5 rounded-full grid place-items-center text-[11px] font-bold shrink-0 ${i === 0 ? 'bg-rose-500 text-white' : i === 1 ? 'bg-rose-200 text-rose-800' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</span>
      <span className="flex-1 min-w-0 truncate text-[13px] text-gray-800">{name}</span>
      <span className="text-[13px] font-semibold text-gray-900 shrink-0">{right}</span>
    </div>
  );
}

export function AttendanceTopCard() {
  const [period, setPeriod] = useState<string | undefined>(undefined);
  const { data, isLoading } = useAttendanceTop(period);
  const periods = data?.periods ?? [];
  const cur = period ?? data?.period;

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-display font-semibold text-ink">สรุปมาสาย / ลางาน</h3>
        {periods.length > 0 && (
          <select
            value={cur ?? ''}
            onChange={(e) => setPeriod(e.target.value)}
            className="text-xs rounded-lg border border-gray-200 px-2 py-1 focus:outline-none focus:border-brand-400"
          >
            {periods.map((p) => <option key={p} value={p}>{periodLabel(p)}</option>)}
          </select>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted py-4 text-center">กำลังโหลด…</p>
      ) : data && data.reconciled === false ? (
        <p className="text-sm text-amber-700 bg-amber-50 rounded-lg py-3 px-3 text-center">
          งวดนี้ยังไม่ได้ตรวจยอด — กด “ตรวจยอด” ของงวดนี้ก่อน จึงจะเห็นสรุปมาสาย/ลางาน
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium text-rose-600 flex items-center gap-1 mb-1"><Clock className="w-3.5 h-3.5" /> มาสายบ่อยสุด</p>
            {(data?.top_late ?? []).length === 0 ? (
              <p className="text-xs text-gray-400 py-2">ไม่มีคนมาสายในงวดนี้ 🎉</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {(data?.top_late ?? []).map((r: AttendanceTopRow, i) => (
                  <RankRow key={r.emp_code} i={i} name={r.name} right={`${r.late_days} ครั้ง${r.late_minutes ? ` · ${r.late_minutes} น.` : ''}`} />
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs font-medium text-amber-600 flex items-center gap-1 mb-1"><CalendarOff className="w-3.5 h-3.5" /> ลางานบ่อยสุด</p>
            {(data?.top_leave ?? []).length === 0 ? (
              <p className="text-xs text-gray-400 py-2">ไม่มีการลาในงวดนี้</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {(data?.top_leave ?? []).map((r: AttendanceTopRow, i) => (
                  <RankRow key={r.emp_code} i={i} name={r.name} right={`${r.leave_days} วัน${r.absent_days ? ` · ขาด ${r.absent_days}` : ''}`} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
